import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { buildApiUrl, fetchApi } from "@neu/libui";

export interface CaseProgramStatuses {
	pap: string;
	biosimilar: string;
	bridge: string;
	cap: string;
	discount340b: string;
}

interface ProgramRow {
	id?: number;
	status?: string | null;
	created_on?: string | Date | null;
	deleted?: boolean;
}

interface CaseDetail {
	id?: number;
	subform_pap?: ProgramRow[] | null;
	subform_biosimilar?: ProgramRow[] | null;
	subform_bridge?: ProgramRow[] | null;
	subform_patient_cap?: ProgramRow[] | null;
	subform_340b?: ProgramRow[] | null;
}

interface EntityListResponse {
	data?: Array<{ id?: number }>;
}

function createdOnMs(row: ProgramRow): number {
	const raw = row.created_on;
	if (raw === null || raw === undefined || raw === "") {
		return 0;
	}
	const ms = raw instanceof Date ? raw.getTime() : new Date(raw).getTime();
	return Number.isFinite(ms) ? ms : 0;
}

/** Newest program row status (by created_on, then id). */
function latestStatus(rows: ProgramRow[] | null | undefined): string {
	let best: { status: string; createdMs: number; id: number } | undefined;
	for (const row of rows ?? []) {
		if (row.deleted === true) {
			continue;
		}
		const status = typeof row.status === "string" ? row.status.trim() : "";
		if (!status) {
			continue;
		}
		const createdMs = createdOnMs(row);
		const rowID = typeof row.id === "number" ? row.id : 0;
		if (
			!best ||
			createdMs > best.createdMs ||
			(createdMs === best.createdMs && rowID > best.id)
		) {
			best = { status, createdMs, id: rowID };
		}
	}
	return best?.status ?? "";
}

function statusesFromCase(detail: CaseDetail): CaseProgramStatuses {
	return {
		pap: latestStatus(detail.subform_pap),
		biosimilar: latestStatus(detail.subform_biosimilar),
		bridge: latestStatus(detail.subform_bridge),
		cap: latestStatus(detail.subform_patient_cap),
		discount340b: latestStatus(detail.subform_340b),
	};
}

async function fetchCaseProgramStatuses(
	patientID: string
): Promise<Map<number, CaseProgramStatuses>> {
	const list = (await fetchApi(
		buildApiUrl(
			`/patient_case/list?_patient_id=eq:${encodeURIComponent(patientID)}&limit=200&sort=-id`
		)
	)) as EntityListResponse;
	const caseIDs = (list.data ?? [])
		.map((row: { id?: number }) => row.id)
		.filter(
			(id: number | undefined): id is number => typeof id === "number"
		);

	const details = await Promise.all(
		caseIDs.map(async (caseID: number) => {
			const detail = (await fetchApi(
				buildApiUrl(`/patient_case/${caseID}`)
			)) as CaseDetail;
			return detail;
		})
	);

	const map = new Map<number, CaseProgramStatuses>();
	for (const detail of details) {
		if (typeof detail?.id !== "number") {
			continue;
		}
		map.set(detail.id, statusesFromCase(detail));
	}
	return map;
}

/**
 * Latest program statuses keyed by patient_case id for the open patient.
 * Loads case details (deep relations / subforms) because program list APIs
 * do not expose patient_case_id in the definition-backed select set.
 */
export function usePatientCaseProgramStatuses(
	patientID: string | undefined
): Map<number, CaseProgramStatuses> {
	const { data } = useQuery({
		queryKey: ["patient-case-program-statuses", patientID ?? null],
		enabled: Boolean(patientID),
		queryFn: () => {
			if (!patientID) {
				return Promise.resolve(new Map<number, CaseProgramStatuses>());
			}
			return fetchCaseProgramStatuses(patientID);
		},
		staleTime: 30_000,
	});

	return useMemo(
		() => data ?? new Map<number, CaseProgramStatuses>(),
		[data]
	);
}
